/** Floating AI chat with confirm-to-apply proposals + ElevenLabs voice (push-to-talk & spoken replies).
 * Reused by EPBCS (defaults) and other modules via props. */
import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, X, PaperPlaneRight, CircleNotch, CheckCircle, XCircle,
         ArrowsClockwise, WarningCircle, Microphone, SpeakerHigh, StopCircle } from "@phosphor-icons/react";

const KIND_LABELS = {
  budget_update: "Budget update", master_create: "New master member",
  master_update: "Master update", project_create: "New CapEx project",
  asset_create: "New asset purchase", cycle_update: "Cycle update",
  deal_create: "New trade deal", deal_update: "Deal update", movement_update: "Movement update",
};

const MODEL_LABELS = {
  "": "Claude Sonnet 4.6", claude: "Claude Sonnet 4.6",
  qwen: "Qwen (DashScope)", allam: "ALLaM (watsonx.ai)",
};
const modelLabel = (engine) => MODEL_LABELS[engine || ""] || engine;

const STR = {
  en: {
    emptyTitle: "Ask me anything about your data",
    micTip: "Tip: press the mic to speak your question.",
    thinking: "Working — reading your data…",
    listening: "Listening… click the mic to stop & send",
    transcribing: "Transcribing your speech…",
    listen: "Listen", stop: "Stop", preparing: "Preparing…",
    proposed: "Proposed change", why: "Why:", confirm: "Confirm & apply", dismiss: "Dismiss",
    applied: "applied", dismissed: "dismissed",
    appliedToast: "Proposal applied", dismissedToast: "Proposal dismissed",
    error: "Sorry — something went wrong. Please try again.",
    micDenied: "Microphone access was denied",
    speechFail: "Could not generate speech",
    transcribeFail: "Transcription failed — please try again",
  },
  ar: {
    emptyTitle: "اسألني أي شيء عن بياناتك",
    micTip: "نصيحة: اضغط على الميكروفون لطرح سؤالك صوتيًا.",
    thinking: "جارٍ العمل — قراءة بياناتك…",
    listening: "جارٍ الاستماع… اضغط الميكروفون للإيقاف والإرسال",
    transcribing: "جارٍ تحويل كلامك إلى نص…",
    listen: "استمع", stop: "إيقاف", preparing: "جارٍ التحضير…",
    proposed: "تغيير مقترح", why: "السبب:", confirm: "تأكيد وتطبيق", dismiss: "تجاهل",
    applied: "تم التطبيق", dismissed: "تم التجاهل",
    appliedToast: "تم تطبيق المقترح", dismissedToast: "تم تجاهل المقترح",
    error: "عذرًا — حدث خطأ ما. يُرجى المحاولة مرة أخرى.",
    micDenied: "تم رفض الوصول إلى الميكروفون",
    speechFail: "تعذّر توليد الصوت",
    transcribeFail: "فشل تحويل الكلام إلى نص — حاول مرة أخرى",
    placeholder: "اسأل عن بياناتك — الموازنات، المشاريع، الصفقات…",
    hint: "«ما إجمالي الإيرادات المدرجة في الموازنة؟» · «اعرض سلم الرواتب للدرجة G3» — التحديثات تنتظر تأكيدك دائمًا.",
    title: "المساعد الذكي",
  },
};

const ProposalCard = ({ p, base, onDecide, t }) => {
  const [busy, setBusy] = useState("");
  const act = async (kind) => {
    setBusy(kind);
    try {
      await api.post(`${base}/${kind}`, { proposal_id: p.id });
      onDecide(p.id, kind === "confirm" ? "applied" : "dismissed");
      toast[kind === "confirm" ? "success" : "info"](
        kind === "confirm" ? t.appliedToast : t.dismissedToast);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(""); }
  };
  const kindLabel = KIND_LABELS[p.kind] || String(p.kind || "").replace(/_/g, " ");
  return (
    <div className="mt-2 border border-[#C8A24B]/50 bg-amber-50/40 rounded-sm p-3"
      data-testid={`ai-proposal-${p.id}`}>
      <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.15em] text-[#8a6d2b]">
        <WarningCircle size={12} weight="fill" /> {t.proposed} — {kindLabel}
      </div>
      <p className="mt-1.5 text-[12px] leading-relaxed text-slate-700">{p.summary}</p>
      {p.reason && <p className="mt-1 text-[11px] italic text-slate-500">{t.why} {p.reason}</p>}
      {p.status === "pending" ? (
        <div className="mt-2.5 flex gap-2">
          <button onClick={() => act("confirm")} disabled={!!busy}
            data-testid={`ai-confirm-${p.id}`}
            className="inline-flex items-center gap-1.5 bg-[#0B1E3F] text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded hover:bg-[#132a52] disabled:opacity-50">
            {busy === "confirm" ? <CircleNotch size={12} className="animate-spin" /> : <CheckCircle size={12} weight="bold" />}
            {t.confirm}
          </button>
          <button onClick={() => act("dismiss")} disabled={!!busy}
            data-testid={`ai-dismiss-${p.id}`}
            className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-600 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded hover:bg-slate-50 disabled:opacity-50">
            <XCircle size={12} weight="bold" /> {t.dismiss}
          </button>
        </div>
      ) : (
        <div className={`mt-2 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider ${p.status === "applied" ? "text-[#1F6B4A]" : "text-slate-400"}`}
          data-testid={`ai-proposal-status-${p.id}`}>
          {p.status === "applied" ? <CheckCircle size={12} weight="fill" /> : <XCircle size={12} weight="fill" />}
          {p.status}
        </div>
      )}
    </div>
  );
};

const renderText = (text) => {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? <b key={i}>{p.slice(2, -2)}</b> : p);
};

const Message = ({ m, idx, base, onDecide, onListen, speech, t }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[85%] px-3 py-2 text-[12.5px] leading-relaxed whitespace-pre-wrap rounded-sm ${
      m.role === "user" ? "bg-[#0B1E3F] text-white" : "bg-white border border-slate-200 text-slate-700"}`}>
      {renderText(m.text)}
      {(m.proposals || []).map((p) => <ProposalCard key={p.id} p={p} base={base} onDecide={onDecide} t={t} />)}
      {m.role === "assistant" && m.text && (
        <div className="flex items-center gap-2">
          <button onClick={() => onListen(idx, m.text)} data-testid={`ai-listen-${idx}`}
            className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-[#0B1E3F] transition-colors">
            {speech.idx === idx && speech.state === "loading"
              ? <CircleNotch size={11} className="animate-spin text-[#C8A24B]" />
              : speech.idx === idx && speech.state === "playing"
                ? <StopCircle size={11} weight="fill" className="text-[#C8A24B]" />
                : <SpeakerHigh size={11} weight="bold" />}
            {speech.idx === idx && speech.state === "playing" ? t.stop
              : speech.idx === idx && speech.state === "loading" ? t.preparing : t.listen}
          </button>
          <span data-testid={`ai-engine-badge-${idx}`}
            className="mt-1.5 text-[9px] font-bold uppercase tracking-wider text-[#8a6d2b] border border-[#C8A24B]/50 bg-amber-50 px-1.5 py-0.5 rounded-sm">
            {modelLabel(m.engine)}
          </span>
        </div>
      )}
    </div>
  </div>
);

export default function AiAssistant({ base = "/epbcs/ai", title = "Planning AI Assistant",
  fabLabel = "Planning AI", storageKey = "epbcs_ai_session",
  placeholder = "Ask about masters, budgets, CapEx, workforce…",
  hint = "“What is total budgeted revenue?” · “Show pay scale for Grade G3” · “Set account 40100 to 50,000 in Jan 2027” — updates always wait for your confirmation." }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [rec, setRec] = useState("idle");
  const [speech, setSpeech] = useState({ idx: null, state: "" });
  const [lang, setLang] = useState(() => localStorage.getItem("vaku_ai_lang") || "en");
  const t = STR[lang] || STR.en;
  const isAr = lang === "ar";
  const toggleLang = () => {
    const next = isAr ? "en" : "ar";
    localStorage.setItem("vaku_ai_lang", next);
    setLang(next);
  };
  const recRef = useRef(null);
  const audioRef = useRef(null);
  const newSess = () => {
    const id = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()));
    localStorage.setItem(storageKey, id);
    return id;
  };
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(storageKey) || newSess());
  const bottomRef = useRef(null);
  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
  const lastEngine = lastAssistant ? lastAssistant.engine : "";

  useEffect(() => {
    if (!open) return;
    api.get(`${base}/history?session_id=${sessionId}`)
      .then((r) => setMessages(r.data.messages || []))
      .catch(() => {});
  }, [open, sessionId, base]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  useEffect(() => () => { audioRef.current?.pause(); recRef.current?.stop?.(); }, []);

  const onDecide = (pid, status) =>
    setMessages((ms) => ms.map((m) => ({
      ...m, proposals: (m.proposals || []).map((p) => p.id === pid ? { ...p, status } : p) })));

  const sendText = async (text) => {
    if (!text || busy) return;
    setMessages((ms) => [...ms, { role: "user", text }]);
    setBusy(true);
    try {
      const r = await api.post(`${base}/chat`, { session_id: sessionId, message: text, lang });
      setMessages((ms) => [...ms, { role: "assistant", text: r.data.reply,
        engine: r.data.engine || "", proposals: r.data.proposals || [] }]);
    } catch (e) {
      setMessages((ms) => [...ms, { role: "assistant",
        text: e?.response?.data?.detail || t.error }]);
    } finally { setBusy(false); }
  };

  const send = () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    sendText(text);
  };

  const toggleMic = async () => {
    if (rec === "recording") { recRef.current?.stop(); return; }
    if (rec !== "idle" || busy) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRec("transcribing");
        try {
          const fd = new FormData();
          fd.append("audio", new Blob(chunks, { type: mr.mimeType || "audio/webm" }), "speech.webm");
          const r = await api.post("/voice/transcribe", fd);
          const text = (r.data.text || "").trim();
          if (text) await sendText(text);
        } catch (e) {
          toast.error(e?.response?.data?.detail || t.transcribeFail);
        } finally { setRec("idle"); }
      };
      recRef.current = mr;
      mr.start();
      setRec("recording");
    } catch {
      toast.error(t.micDenied);
    }
  };

  const listen = async (idx, text) => {
    if (speech.idx === idx && speech.state === "playing") {
      audioRef.current?.pause();
      audioRef.current = null;
      setSpeech({ idx: null, state: "" });
      return;
    }
    audioRef.current?.pause();
    setSpeech({ idx, state: "loading" });
    try {
      const r = await api.post("/voice/tts", { text }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => { setSpeech({ idx: null, state: "" }); URL.revokeObjectURL(url); };
      await a.play();
      setSpeech({ idx, state: "playing" });
    } catch {
      toast.error(t.speechFail);
      setSpeech({ idx: null, state: "" });
    }
  };

  const reset = () => {
    audioRef.current?.pause();
    setSpeech({ idx: null, state: "" });
    setSessionId(newSess());
    setMessages([]);
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="ai-assistant-fab"
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-[#0B1E3F] text-white pl-4 pr-5 py-3 rounded-full shadow-xl border border-[#C8A24B]/60 hover:bg-[#132a52] transition-colors">
          <Sparkle size={18} weight="fill" className="text-[#C8A24B]" />
          <span className="text-[12px] font-bold uppercase tracking-wider">{fabLabel}</span>
        </button>
      )}
      {open && (
        <div dir={isAr ? "rtl" : "ltr"}
          className="fixed bottom-6 right-6 z-40 w-[420px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-6rem)] flex flex-col bg-[#F8F9FA] border border-slate-300 shadow-2xl rounded-sm overflow-hidden"
          data-testid="ai-assistant-panel">
          <div className="flex-none flex items-center justify-between bg-[#0B1E3F] px-4 py-3">
            <div className="flex items-center gap-2">
              <Sparkle size={16} weight="fill" className="text-[#C8A24B]" />
              <span className="text-[12px] font-bold uppercase tracking-[0.15em] text-white">{isAr ? STR.ar.title : title}</span>
              <span data-testid="ai-header-model-badge"
                className="text-[9px] font-bold uppercase tracking-wider text-[#C8A24B] border border-[#C8A24B]/50 px-1.5 py-0.5 rounded-sm">
                {modelLabel(lastEngine)}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={toggleLang} data-testid="ai-lang-toggle"
                title={isAr ? "Switch to English" : "التبديل إلى العربية"}
                className="px-2 py-1 text-[11px] font-bold text-gray-300 border border-white/25 rounded-sm hover:text-white hover:border-white/60 transition-colors">
                {isAr ? "EN" : "ع"}
              </button>
              <button onClick={reset} title={isAr ? "محادثة جديدة" : "New chat"} data-testid="ai-new-chat-btn"
                className="p-1.5 text-gray-400 hover:text-white"><ArrowsClockwise size={15} weight="bold" /></button>
              <button onClick={() => setOpen(false)} data-testid="ai-close-btn"
                className="p-1.5 text-gray-400 hover:text-white"><X size={15} weight="bold" /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3" data-testid="ai-message-list">
            {messages.length === 0 && !busy && (
              <div className="text-center mt-10 px-6">
                <Sparkle size={26} weight="fill" className="text-[#C8A24B] mx-auto" />
                <p className="mt-3 text-[13px] font-semibold text-[#0B1E3F]">{t.emptyTitle}</p>
                <p className="mt-1.5 text-[11.5px] leading-relaxed text-slate-500">{isAr ? STR.ar.hint : hint}</p>
                <p className="mt-2 text-[10.5px] text-slate-400 inline-flex items-center gap-1">
                  <Microphone size={11} weight="bold" /> {t.micTip}
                </p>
              </div>
            )}
            {messages.map((m, i) => (
              <Message key={i} m={m} idx={i} base={base} onDecide={onDecide}
                onListen={listen} speech={speech} t={t} />
            ))}
            {busy && (
              <div className="flex items-center gap-2 text-[11px] text-slate-500 px-1" data-testid="ai-thinking">
                <CircleNotch size={13} className="animate-spin text-[#C8A24B]" /> {t.thinking}
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="flex-none border-t border-slate-200 bg-white p-2.5 flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={rec === "recording" ? t.listening
                : rec === "transcribing" ? t.transcribing : (isAr ? STR.ar.placeholder : placeholder)}
              disabled={rec !== "idle"}
              data-testid="ai-chat-input"
              className="flex-1 px-3 py-2 text-[12.5px] border border-slate-200 rounded-sm focus:outline-none focus:border-[#C8A24B] disabled:bg-slate-50" />
            <button onClick={toggleMic} disabled={busy || rec === "transcribing"}
              data-testid="ai-mic-btn"
              title={rec === "recording" ? "Stop & send" : "Speak your question"}
              className={`p-2.5 rounded-sm border transition-colors disabled:opacity-40 ${
                rec === "recording"
                  ? "bg-red-600 border-red-600 text-white animate-pulse"
                  : "border-slate-200 text-slate-500 hover:text-[#0B1E3F] hover:border-[#C8A24B]"}`}>
              {rec === "transcribing" ? <CircleNotch size={15} className="animate-spin" />
                : rec === "recording" ? <StopCircle size={15} weight="fill" />
                : <Microphone size={15} weight="bold" />}
            </button>
            <button onClick={send} disabled={!input.trim() || busy || rec !== "idle"} data-testid="ai-send-btn"
              className="p-2.5 bg-[#0B1E3F] text-white rounded-sm hover:bg-[#132a52] disabled:opacity-40">
              <PaperPlaneRight size={15} weight="fill" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
